import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Check, Filter, Search, SlidersHorizontal } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { useIsMobile } from '@/hooks/use-mobile';
import type { ConversationBillingFilter } from '@/utils/conversationBilling';
import type { ConversationListParams } from '@/hooks/useConversationListParams';

interface ConversationActionBarProps {
  params: ConversationListParams;
  onChange: (next: Partial<ConversationListParams>) => void;
  /** Hidden when the agent has no non-billable calls to separate out. */
  showBillingFilter?: boolean;
  resultCount: number;
  totalCount: number;
}

const BILLING_FILTERS: Array<{
  value: ConversationBillingFilter;
  labelKey: string;
  fallback: string;
}> = [
  {
    value: 'all',
    labelKey: 'agent.conversations.filters.billing.all',
    fallback: 'All',
  },
  {
    value: 'billable',
    labelKey: 'agent.conversations.filters.billing.billable',
    fallback: 'Counted',
  },
  {
    value: 'non_billable',
    labelKey: 'agent.conversations.filters.billing.non_billable',
    fallback: 'Not counted',
  },
];

const QUICK_RANGES = [
  { id: 'all', labelKey: 'agent.conversations.filters.all', fallback: 'All' },
  {
    id: 'today',
    labelKey: 'agent.conversations.filters.today',
    fallback: 'Today',
  },
  {
    id: 'lastWeek',
    labelKey: 'agent.conversations.filters.last_week',
    fallback: 'Last Week',
  },
  {
    id: 'lastMonth',
    labelKey: 'agent.conversations.filters.last_month',
    fallback: 'Last Month',
  },
  {
    id: 'lastYear',
    labelKey: 'agent.conversations.filters.last_year',
    fallback: 'Last Year',
  },
];

/** Everything back to defaults, which is also what the URL omits entirely. */
const CLEARED_PARAMS = {
  search: '',
  billing: 'all' as ConversationBillingFilter,
  callback: false,
  dateRange: { from: undefined, to: undefined },
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const endOfToday = () => {
  const end = startOfToday();
  end.setHours(23, 59, 59, 999);
  return end;
};

const rangeFor = (id: string) => {
  const today = startOfToday();
  const end = endOfToday();

  switch (id) {
    case 'today':
      return { from: today, to: end };
    case 'lastWeek': {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from, to: end };
    }
    case 'lastMonth': {
      const from = new Date(today);
      from.setMonth(from.getMonth() - 1);
      return { from, to: end };
    }
    case 'lastYear': {
      const from = new Date(today);
      from.setFullYear(from.getFullYear() - 1);
      return { from, to: end };
    }
    default:
      return { from: undefined, to: undefined };
  }
};

/** Which quick range, if any, the current dates correspond to. */
const activeRangeId = (from: Date | undefined, to: Date | undefined) => {
  if (!from && !to) return 'all';
  if (!from || !to) return 'custom';

  for (const { id } of QUICK_RANGES) {
    if (id === 'all') continue;
    const candidate = rangeFor(id);
    if (
      candidate.from?.toDateString() === from.toDateString() &&
      candidate.to?.toDateString() === to.toDateString()
    ) {
      return id;
    }
  }
  return 'custom';
};

export const ConversationActionBar: React.FC<ConversationActionBarProps> = ({
  params,
  onChange,
  showBillingFilter = false,
  resultCount,
  totalCount,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Typing updates the URL on a delay: the filter reads every message of every
  // conversation, and rewriting the query string per keystroke would also bury
  // the history stack.
  const [searchDraft, setSearchDraft] = useState(params.search);

  useEffect(() => {
    setSearchDraft(params.search);
  }, [params.search]);

  useEffect(() => {
    if (searchDraft === params.search) return;
    const timer = setTimeout(() => onChange({ search: searchDraft }), 400);
    return () => clearTimeout(timer);
  }, [searchDraft, params.search, onChange]);

  const activeRange = activeRangeId(params.dateRange.from, params.dateRange.to);

  const handleCustomDateSelect = (range: {
    from: Date | undefined;
    to: Date | undefined;
  }) => {
    const cap = endOfToday();
    const clamp = (date: Date | undefined) =>
      date ? (date > cap ? cap : date) : undefined;

    const from = clamp(range?.from);
    const to = clamp(range?.to ?? range?.from);
    onChange({ dateRange: { from: from && to && from > to ? to : from, to } });
  };

  const isFiltered =
    Boolean(params.search) ||
    params.billing !== 'all' ||
    params.callback ||
    Boolean(params.dateRange.from || params.dateRange.to);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          className={`flex ${
            isMobile ? 'flex-wrap' : 'flex-row'
          } min-w-0 flex-1 gap-2 overflow-x-auto pb-1`}
        >
          {QUICK_RANGES.map(({ id, labelKey, fallback }) => (
            <Button
              key={id}
              variant={activeRange === id ? 'brand' : 'outline'}
              size="sm"
              onClick={() => onChange({ dateRange: rangeFor(id) })}
              className={`text-sm ${isMobile ? 'flex-shrink-0' : ''}`}
            >
              {t(labelKey, fallback)}
            </Button>
          ))}
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={activeRange === 'custom' ? 'brand' : 'outline'}
                size="sm"
                className={`text-sm ${isMobile ? 'flex-shrink-0' : ''}`}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('agent.conversations.filters.custom', 'Custom')}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto min-w-[280px] p-3"
              align={isMobile ? 'start' : 'end'}
            >
              <div className="flex flex-col space-y-2">
                <Label>
                  {t(
                    'agent.conversations.action_bar.date_range.translation',
                    'Date Range'
                  )}
                </Label>
                <Calendar
                  className="self-center"
                  mode="range"
                  disabled={date => date > startOfToday()}
                  selected={{
                    from: params.dateRange.from,
                    to: params.dateRange.to,
                  }}
                  onSelect={range =>
                    handleCustomDateSelect({
                      from: range?.from || undefined,
                      to: range?.to || range?.from || undefined,
                    })
                  }
                  numberOfMonths={1}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="relative min-w-0 flex-1 sm:w-56 sm:flex-none">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              placeholder={t(
                'agent.conversations.search.placeholder.translation',
                'Search what was said'
              )}
              aria-label={t(
                'agent.conversations.search.label.translation',
                'Search conversations'
              )}
              className="h-9 bg-card pl-8"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={
                  params.billing === 'all' && !params.callback
                    ? 'outline'
                    : 'brand'
                }
                size="sm"
                className="shrink-0"
                aria-label={t(
                  'agent.conversations.filters.billing.label',
                  'Usage'
                )}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1.5">
              {showBillingFilter && (
                <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                  {t('agent.conversations.filters.billing.label', 'Usage')}
                </p>
              )}
              {showBillingFilter &&
                BILLING_FILTERS.map(({ value, labelKey, fallback }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onChange({ billing: value })}
                    className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                  >
                    {t(labelKey, fallback)}
                    {params.billing === value && (
                      <Check className="h-4 w-4 text-brand" aria-hidden />
                    )}
                  </button>
                ))}
              {showBillingFilter && <div className="my-1 h-px bg-border" />}
              <button
                type="button"
                onClick={() => onChange({ callback: !params.callback })}
                className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                {t(
                  'agent.conversations.filters.needs_callback.translation',
                  'Needs callback'
                )}
                {params.callback && (
                  <Check className="h-4 w-4 text-brand" aria-hidden />
                )}
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {isFiltered && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-xs text-muted-foreground">
            {t('agent.conversations.search.results.translation', {
              defaultValue: '{{count}} of {{total}} conversations',
              count: resultCount,
              total: totalCount,
            })}
          </span>
          <Button
            variant="transparent"
            size="sm"
            className="h-auto px-1 py-0 text-xs"
            onClick={() => onChange(CLEARED_PARAMS)}
          >
            {t('agent.conversations.filters.clear.translation', 'Clear')}
          </Button>
        </div>
      )}
    </div>
  );
};
